/**
 * Purpose: {@link RealtimeDriver} — browser-side counterpart to the
 *          server's `llm.realtime.session.create` handler. Mints an
 *          ephemeral OpenAI token via `client.realtime.session.create`,
 *          opens a WebRTC peer connection straight to
 *          `api.openai.com/v1/realtime`, attaches the user's microphone
 *          and an `<audio>` element for playback, and surfaces the
 *          datachannel as a stream of high-level events
 *          (`transcript`, `tool-call`, `tool-result`, `usage`,
 *          `status`, `error`).
 *
 * Implementation notes:
 *   - Browser-only. WebRTC types come from the DOM lib (see this
 *     package's tsconfig.json). Importing from Node will compile but
 *     `connect()` will throw because `globalThis.RTCPeerConnection` is
 *     undefined.
 *   - Server tools (`serverTools: string[]`) are *not* executed in the
 *     browser. The server bakes their JSON Schemas into the realtime
 *     session config at mint time; on a function-call event we look up
 *     the tool name in the local `ClientToolRegistry` first and, if
 *     absent, bridge through `client.realtime.tool.execute(...)` so
 *     server execution stays server-side.
 *   - Cost capture happens via `response.done` events: we parse the
 *     `usage` block and fire `client.realtime.usage.report(...)`. The
 *     client wrapper swallows transport errors so the WebRTC event loop
 *     never blocks on telemetry; we re-surface them as `error` events
 *     for the host to log if it wants to.
 *   - We don't own the audio element. If the caller passes one, we
 *     attach the remote audio track to it; otherwise we create a
 *     hidden one and append it to `document.body` so playback works
 *     out of the box.
 *   - Single connect/disconnect per instance. To restart a session,
 *     create a new driver. This keeps state machines simple and matches
 *     the OpenAI session lifetime (each ephemeral token mints one
 *     session).
 *
 * Created: 2026-05-16 13:00
 *
 * Changelog:
 *   - 2026-05-16 13:00 — Initial version. V1 driver covering the
 *     session-mint → SDP exchange → datachannel dispatch flow plus the
 *     three event types every host SPA needs (`transcript`,
 *     `tool-call`, `usage`).
 *   - 2026-05-18 20:10 — Fix: drop the `?model=<id>` query string from
 *     the SDP exchange URL. That marker came from the OpenAI Realtime
 *     Beta API and the GA endpoint now rejects it with
 *     `400 The Realtime Beta API is no longer supported. Please use
 *     /v1/realtime for the GA API.` In the GA flow the model is implicit
 *     in the ephemeral client_secret, so no query parameters are needed.
 *   - 2026-05-18 20:30 — Fix (continued): dropping the query was not
 *     enough — the bare `/v1/realtime` path is itself the beta SDP
 *     endpoint and OpenAI returns the same `beta_api_shape_disabled`
 *     error regardless of query string. The GA path for WebRTC SDP
 *     exchange is `/v1/realtime/calls`. Updated OPENAI_REALTIME_URL.
 *   - 2026-05-18 21:25 — Expand `RealtimeDriverOptions` with the full
 *     GA session-config surface mirrored from `@ada/llm`: audioInput /
 *     audioOutput / toolChoice / outputModalities / temperature /
 *     maxOutputTokens / tracing / prompt / include / sessionOverrides
 *     / expiresInSeconds. All forwarded verbatim through
 *     `llm.realtime.session.create`.
 *   - 2026-06-01 13:30 — Add `updateClientTools()` for mid-session tool
 *     changes. The driver now owns a mutable client-tool registry and
 *     caches the server-tool schemas returned by the mint, so a
 *     `session.update` can re-advertise the full tool list (server +
 *     client) without dropping server tools, and local execution lookups
 *     resolve against the updated set. Server tools remain fixed at mint.
 *   - 2026-10-01 15:40 — Fix: also handle the GA assistant-transcript events
 *     `response.output_audio_transcript.delta` / `.done`. The driver only
 *     knew the beta `response.audio_transcript.*` names, so on GA sessions
 *     assistant transcripts and the 'speaking' status were never emitted.
 *     The beta names stay as aliases so the change is additive.
 *   - 2026-10-06 16:10 — Additive surface for the in-chat realtime voice
 *     mode of @ada/copilot-widget (`voice="realtime"`): new events
 *     `speech-started`, `input-committed`, `output-audio` (WebRTC
 *     `output_audio_buffer.*`) and `response-done`; optional `itemId` on
 *     transcript events; `sendText()` for typed turns; `interrupt()` now
 *     also clears the WebRTC output buffer and only cancels a response that
 *     is actually running; outbound events are queued until the datachannel
 *     opens instead of being dropped; the mic asks for echo cancellation
 *     explicitly. Fix: `connect()` without WebRTC now emits the `error`
 *     event (and 'error' status) before rejecting, as documented — the
 *     voice panel used to show nothing. Existing status semantics are
 *     unchanged, so `<VoiceWidget>` behaves as before.
 *   - 2026-10-06 17:40 — Fix: `disconnect()` during `connect()` leaked the
 *     peer connection and the microphone — `connect()` kept going after its
 *     awaits and created them after the teardown. It now checks for a
 *     disconnect after every await, releases anything created late, and
 *     rejects without emitting an error (the caller asked for it).
 */
import { ClientToolRegistry } from './client-tool-registry.js';
// ── Constants ───────────────────────────────────────────────────
/**
 * OpenAI Realtime GA SDP endpoint. The browser POSTs its SDP offer here
 * with the ephemeral client_secret as bearer; OpenAI returns the SDP
 * answer as plain text/sdp. The bare `/v1/realtime` path is the legacy
 * BETA SDP endpoint and now responds with
 * `400 The Realtime Beta API is no longer supported. Please use
 * /v1/realtime for the GA API.` — that error message is OpenAI's
 * somewhat-confusing way of saying "use the GA family", and the
 * specific GA path for WebRTC SDP exchange is `/v1/realtime/calls`.
 */
const OPENAI_REALTIME_URL = 'https://api.openai.com/v1/realtime/calls';
// ── RealtimeDriver class ────────────────────────────────────────
export class RealtimeDriver {
    #opts;
    #listeners = new Set();
    #pc;
    #dc;
    #localStream;
    #audioElement;
    /** True iff this driver constructed (and must clean up) the audio element. */
    #ownsAudioElement = false;
    #sessionId;
    #model;
    #status = 'idle';
    #closed = false;
    #cumulativeUsd = 0;
    /**
     * Mutable client-tool registry. Initialised from `opts.toolRegistry`,
     * but `updateClientTools()` swaps in a new one mid-session so both the
     * tool list advertised to OpenAI (via `session.update`) and the local
     * execution lookup (in `#dispatchFunctionCall`) stay in sync.
     */
    #clientToolRegistry;
    /**
     * Server tools resolved at mint time, in OpenAI's function shape. Cached
     * so `updateClientTools()` can re-send the *full* tool list without
     * dropping server tools (a `session.update` replaces the list wholesale).
     */
    #serverToolSchemas = [];
    /**
     * Events sent before the datachannel opened. `connect()` resolves once
     * the SDP answer is in, which can be before the channel is open; without
     * this queue the first `session.update` / typed turn would be lost.
     */
    #outbox = [];
    /** A model response is being generated (`response.created` → `response.done`). */
    #responseActive = false;
    /** Assistant audio is playing over WebRTC (`output_audio_buffer.started` → stopped/cleared). */
    #audioPlaying = false;
    /** Cumulative input + output tokens reported so far (debug-friendly). */
    #totalInputTokens = 0;
    #totalOutputTokens = 0;
    constructor(opts) {
        this.#opts = opts;
        this.#clientToolRegistry = opts.toolRegistry;
    }
    // ── Public API ────────────────────────────────────────────────
    get status() {
        return this.#status;
    }
    /** ada-side correlation id (the one returned by `session.create`). */
    get sessionId() {
        return this.#sessionId;
    }
    /** Total input/output tokens reported so far (debug-friendly). */
    get totalInputTokens() {
        return this.#totalInputTokens;
    }
    get totalOutputTokens() {
        return this.#totalOutputTokens;
    }
    /** Subscribe to driver events. Returns an `unsubscribe` function. */
    on(listener) {
        this.#listeners.add(listener);
        return () => {
            this.#listeners.delete(listener);
        };
    }
    /**
     * Mint an ephemeral token, open the WebRTC peer connection to
     * OpenAI, attach mic + audio output, and start the datachannel
     * dispatcher. Idempotent if already connected.
     */
    async connect() {
        if (this.#pc)
            return;
        if (this.#closed)
            throw new Error('RealtimeDriver: already closed');
        if (typeof globalThis.RTCPeerConnection === 'undefined') {
            const message = 'RealtimeDriver: WebRTC is not available in this runtime. ' +
                'The realtime driver is browser-only.';
            this.#emit({ type: 'error', code: 'webrtc-unavailable', message });
            this.#setStatus('error');
            throw new Error(message);
        }
        this.#setStatus('connecting');
        try {
            const o = this.#opts;
            const session = await o.llm.realtime.session.create({
                ...(o.model !== undefined ? { model: o.model } : {}),
                ...(o.instructions !== undefined ? { instructions: o.instructions } : {}),
                ...(o.serverTools !== undefined ? { serverTools: o.serverTools } : {}),
                clientTools: this.#clientToolRegistry.list().map((t) => ({
                    name: t.name,
                    description: t.description,
                    parameters: t.parameters,
                })),
                // ── GA-shaped session fields ────────────────────────────
                ...(o.toolChoice !== undefined ? { toolChoice: o.toolChoice } : {}),
                ...(o.outputModalities !== undefined ? { outputModalities: o.outputModalities } : {}),
                ...(o.temperature !== undefined ? { temperature: o.temperature } : {}),
                ...(o.maxOutputTokens !== undefined ? { maxOutputTokens: o.maxOutputTokens } : {}),
                ...(o.tracing !== undefined ? { tracing: o.tracing } : {}),
                ...(o.prompt !== undefined ? { prompt: o.prompt } : {}),
                ...(o.include !== undefined ? { include: o.include } : {}),
                ...(o.audioInput !== undefined ? { audioInput: o.audioInput } : {}),
                ...(o.audioOutput !== undefined ? { audioOutput: o.audioOutput } : {}),
                ...(o.sessionOverrides !== undefined ? { sessionOverrides: o.sessionOverrides } : {}),
                ...(o.expiresInSeconds !== undefined ? { expiresInSeconds: o.expiresInSeconds } : {}),
                // ── Back-compat ────────────────────────────────────────
                ...(o.voice !== undefined ? { voice: o.voice } : {}),
                ...(o.maxResponseOutputTokens !== undefined
                    ? { maxResponseOutputTokens: o.maxResponseOutputTokens }
                    : {}),
                ...(o.maxSessionSeconds !== undefined ? { maxSessionSeconds: o.maxSessionSeconds } : {}),
            });
            this.#throwIfAborted();
            this.#sessionId = session.sessionId;
            this.#model = session.model;
            // Cache the server-resolved tool schemas so a later
            // `updateClientTools()` can re-advertise the full tool list.
            this.#serverToolSchemas = session.serverToolSchemas ?? [];
            // The WebRTC negotiation: mic → peer connection → SDP offer →
            // OpenAI SDP answer → datachannel ready. OpenAI returns the answer
            // as plain text/sdp, NOT as a JSON envelope.
            const pc = new RTCPeerConnection(session.iceServers ? { iceServers: session.iceServers } : {});
            this.#pc = pc;
            pc.onconnectionstatechange = () => {
                if (this.#closed)
                    return;
                const s = pc.connectionState;
                if (s === 'failed' || s === 'disconnected' || s === 'closed') {
                    this.#emit({
                        type: 'error',
                        code: 'peer-connection',
                        message: `WebRTC peer connection state: ${s}`,
                    });
                }
            };
            pc.ontrack = (event) => {
                const audio = this.#ensureAudioElement();
                if (event.streams[0]) {
                    audio.srcObject = event.streams[0];
                    void audio.play().catch(() => {
                        // Autoplay rejected — host should call connect() from a
                        // user-gesture handler. Surface as a status event.
                        this.#emit({
                            type: 'error',
                            code: 'autoplay-blocked',
                            message: 'Browser blocked audio playback. Call connect() from a user gesture.',
                        });
                    });
                }
            };
            const dc = pc.createDataChannel('oai-events');
            this.#dc = dc;
            dc.onmessage = (msg) => this.#handleDatachannelMessage(msg);
            dc.onopen = () => this.#flushOutbox();
            dc.onerror = () => {
                this.#emit({
                    type: 'error',
                    code: 'datachannel',
                    message: 'WebRTC datachannel error',
                });
            };
            dc.onclose = () => {
                if (!this.#closed) {
                    this.#setStatus('closed');
                }
            };
            // Acquire mic + add track.
            // Echo cancellation is what lets the user talk over the assistant on
            // loudspeakers: the remote WebRTC audio is the canceller's reference.
            // Browsers default these to on; asking explicitly guards against a
            // host page that changed the defaults.
            const stream = this.#opts.mediaStream ??
                (await navigator.mediaDevices.getUserMedia({
                    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
                }));
            this.#localStream = stream;
            this.#throwIfAborted();
            for (const track of stream.getAudioTracks()) {
                pc.addTrack(track, stream);
            }
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            this.#throwIfAborted();
            if (!offer.sdp) {
                throw new Error('Local SDP offer is empty');
            }
            // GA SDP endpoint is `/v1/realtime/calls` (no query string). The
            // model is baked into the ephemeral client_secret at mint time.
            // See OPENAI_REALTIME_URL above for the beta→GA error semantics.
            const sdpResponse = await fetch(OPENAI_REALTIME_URL, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.clientSecret.value}`,
                    'Content-Type': 'application/sdp',
                },
                body: offer.sdp,
            });
            if (!sdpResponse.ok) {
                const text = await safeText(sdpResponse);
                throw new Error(`OpenAI SDP exchange failed (${sdpResponse.status}): ${text}`);
            }
            this.#throwIfAborted();
            const answerSdp = await sdpResponse.text();
            await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
            this.#throwIfAborted();
            this.#setStatus('listening');
        }
        catch (err) {
            if (this.#closed) {
                // disconnect() ran while we were connecting: release whatever was
                // created after it, and don't report the caller's own hang-up.
                this.#releaseResources();
                throw err;
            }
            this.#emit({
                type: 'error',
                message: err.message,
            });
            this.#setStatus('error');
            await this.disconnect();
            throw err;
        }
    }
    /**
     * Tear down the peer connection and release local resources. Safe
     * to call multiple times; subsequent calls are no-ops.
     */
    async disconnect() {
        if (this.#closed)
            return;
        this.#closed = true;
        this.#outbox.length = 0;
        this.#releaseResources();
        this.#setStatus('closed');
    }
    /** Close the datachannel / peer connection, stop the mic, drop the audio element. */
    #releaseResources() {
        try {
            this.#dc?.close();
        }
        catch {
            /* ignore */
        }
        this.#dc = undefined;
        try {
            this.#pc?.close();
        }
        catch {
            /* ignore */
        }
        this.#pc = undefined;
        if (this.#localStream) {
            for (const track of this.#localStream.getTracks()) {
                try {
                    track.stop();
                }
                catch {
                    /* ignore */
                }
            }
            this.#localStream = undefined;
        }
        if (this.#audioElement) {
            try {
                this.#audioElement.pause();
                this.#audioElement.srcObject = null;
            }
            catch {
                /* ignore */
            }
            if (this.#ownsAudioElement && this.#audioElement.parentNode) {
                this.#audioElement.parentNode.removeChild(this.#audioElement);
            }
            this.#audioElement = undefined;
            this.#ownsAudioElement = false;
        }
    }
    /** Used inside `connect()` after each await. */
    #throwIfAborted() {
        if (this.#closed)
            throw new Error('RealtimeDriver: disconnected while connecting');
    }
    /** Mute or unmute the outgoing microphone track. */
    mute(state) {
        if (!this.#localStream)
            return;
        for (const track of this.#localStream.getAudioTracks()) {
            track.enabled = !state;
        }
    }
    /**
     * Interrupt the assistant's current utterance: cancel the response that
     * is being generated (if any) and clear the WebRTC output buffer so audio
     * already generated stops playing too. No-op when nothing is running —
     * OpenAI answers a stray `response.cancel` with an error event.
     */
    interrupt() {
        if (!this.#dc || this.#dc.readyState !== 'open')
            return;
        this.#cutReply();
    }
    /**
     * Add a typed user message to the live conversation and ask for a reply
     * (spoken, with the session's output modalities). A reply that is still
     * running is cut first, like a spoken barge-in. Queued until the
     * datachannel opens.
     */
    sendText(text) {
        const trimmed = text.trim();
        if (!trimmed || !this.#dc || this.#closed)
            return;
        this.#cutReply();
        this.#send({
            type: 'conversation.item.create',
            item: {
                type: 'message',
                role: 'user',
                content: [{ type: 'input_text', text: trimmed }],
            },
        });
        this.#send({ type: 'response.create' });
    }
    /**
     * Replace the client-tool set on the *live* session. Updates the local
     * registry (so subsequent function calls resolve against the new tools)
     * and, if the datachannel is open, emits a `session.update` advertising
     * the full tool list — the cached server tools plus the new client
     * tools. A `session.update` replaces OpenAI's tool list wholesale, so
     * re-sending the server tools here is what keeps them available.
     *
     * Server tools (`opts.serverTools`) are fixed at mint and NOT changed
     * by this method — their schemas live server-side. To change the
     * server-tool set, reconnect with a new driver.
     *
     * No-op-on-the-wire before the datachannel opens: the registry is still
     * updated, but the new tools were already baked into the mint, so the
     * update only matters once the session is live and the set changes.
     */
    updateClientTools(tools) {
        const registry = new ClientToolRegistry();
        for (const t of tools)
            registry.register(t);
        this.#clientToolRegistry = registry;
        if (!this.#dc || this.#dc.readyState !== 'open')
            return;
        this.#send({
            type: 'session.update',
            session: {
                type: 'realtime',
                tools: [
                    ...this.#serverToolSchemas,
                    ...registry.list().map((t) => ({
                        type: 'function',
                        name: t.name,
                        description: t.description,
                        parameters: t.parameters,
                    })),
                ],
            },
        });
    }
    // ── Internal: datachannel dispatch ──────────────────────────
    #handleDatachannelMessage(msg) {
        let event;
        try {
            event = JSON.parse(typeof msg.data === 'string' ? msg.data : '');
        }
        catch (err) {
            this.#emit({
                type: 'error',
                code: 'parse',
                message: `Failed to parse datachannel message: ${err.message}`,
            });
            return;
        }
        switch (event.type) {
            // GA name first; the beta name is kept so nothing that still
            // emits it regresses. OpenAI sends exactly one of the two.
            case 'response.output_audio_transcript.delta':
            case 'response.audio_transcript.delta': {
                const delta = typeof event.delta === 'string' ? event.delta : '';
                if (delta) {
                    this.#setStatus('speaking');
                    this.#emit({
                        type: 'transcript',
                        role: 'assistant',
                        delta,
                        final: false,
                        ...itemIdOf(event),
                        ...responseIdOf(event),
                    });
                }
                return;
            }
            case 'response.output_audio_transcript.done':
            case 'response.audio_transcript.done': {
                const text = typeof event.transcript === 'string' ? event.transcript : '';
                this.#emit({
                    type: 'transcript',
                    role: 'assistant',
                    delta: text,
                    final: true,
                    ...itemIdOf(event),
                    ...responseIdOf(event),
                });
                return;
            }
            case 'conversation.item.input_audio_transcription.completed': {
                const text = typeof event.transcript === 'string' ? event.transcript : '';
                // An empty transcript (noise, a cough) is passed on only when it
                // names its item, so a host waiting on that item can let go of it.
                // Hosts that don't track items never see it.
                if (text || typeof event.item_id === 'string') {
                    this.#emit({
                        type: 'transcript',
                        role: 'user',
                        delta: text,
                        final: true,
                        ...itemIdOf(event),
                    });
                }
                return;
            }
            case 'conversation.item.input_audio_transcription.failed': {
                // Same release signal as an empty transcript: the turn is done and
                // has no text.
                if (typeof event.item_id === 'string' && event.item_id) {
                    this.#emit({
                        type: 'transcript',
                        role: 'user',
                        delta: '',
                        final: true,
                        itemId: event.item_id,
                    });
                }
                return;
            }
            case 'input_audio_buffer.committed': {
                const itemId = typeof event.item_id === 'string' ? event.item_id : '';
                if (itemId) {
                    this.#emit({
                        type: 'input-committed',
                        itemId,
                        ...(typeof event.previous_item_id === 'string'
                            ? { previousItemId: event.previous_item_id }
                            : {}),
                    });
                }
                return;
            }
            case 'response.created': {
                this.#responseActive = true;
                return;
            }
            case 'output_audio_buffer.started':
            case 'output_audio_buffer.stopped':
            case 'output_audio_buffer.cleared': {
                const state = event.type.slice('output_audio_buffer.'.length);
                this.#audioPlaying = state === 'started';
                this.#emit({
                    type: 'output-audio',
                    state,
                    ...responseIdOf(event),
                });
                return;
            }
            case 'response.function_call_arguments.done': {
                const name = typeof event.name === 'string' ? event.name : '';
                const callId = typeof event.call_id === 'string' ? event.call_id : undefined;
                const argsRaw = typeof event.arguments === 'string' ? event.arguments : '{}';
                let args = {};
                try {
                    args = JSON.parse(argsRaw);
                }
                catch (err) {
                    this.#emit({
                        type: 'error',
                        code: 'function-args',
                        message: `Failed to parse function arguments for "${name}": ${err.message}`,
                    });
                    return;
                }
                void this.#dispatchFunctionCall(name, args, callId);
                return;
            }
            case 'response.done': {
                this.#responseActive = false;
                this.#setStatus('listening');
                const response = event.response;
                if (typeof response?.status === 'string') {
                    this.#emit({
                        type: 'response-done',
                        status: response.status,
                        ...(typeof response.id === 'string' ? { responseId: response.id } : {}),
                    });
                }
                const usage = response?.usage;
                if (usage && typeof usage === 'object') {
                    void this.#reportUsage(usage);
                }
                return;
            }
            case 'input_audio_buffer.speech_started': {
                this.#setStatus('listening');
                this.#emit({ type: 'speech-started', ...itemIdOf(event) });
                return;
            }
            case 'input_audio_buffer.speech_stopped': {
                // Model will start responding soon; status flips to 'speaking'
                // on the first transcript delta.
                return;
            }
            case 'error': {
                const err = event.error;
                this.#emit({
                    type: 'error',
                    ...(err?.code ? { code: err.code } : {}),
                    message: err?.message ?? 'Unknown realtime error',
                });
                return;
            }
            default:
                // Unknown event types are ignored — OpenAI may add new event
                // kinds we haven't modelled yet. Keep the channel resilient.
                return;
        }
    }
    async #dispatchFunctionCall(name, args, callId) {
        this.#setStatus('tool');
        // Look up in the local client-tool registry first.
        const localTool = this.#clientToolRegistry.list().find((t) => t.name === name);
        let result;
        let isError = false;
        let errorMessage;
        const origin = localTool ? 'client' : 'server';
        this.#emit({ type: 'tool-call', name, arguments: args, origin });
        if (localTool) {
            try {
                result = await localTool.execute(args);
            }
            catch (err) {
                isError = true;
                errorMessage = err.message;
            }
        }
        else if (!this.#sessionId) {
            isError = true;
            errorMessage = 'Cannot bridge server tool: session id missing';
        }
        else {
            try {
                const bridgeResult = await this.#opts.llm.realtime.tool.execute({
                    sessionId: this.#sessionId,
                    toolName: name,
                    arguments: args,
                });
                result = bridgeResult.result;
                if (bridgeResult.isError) {
                    isError = true;
                    errorMessage = bridgeResult.errorMessage;
                }
            }
            catch (err) {
                isError = true;
                errorMessage = err.message;
            }
        }
        this.#emit({
            type: 'tool-result',
            name,
            result,
            ...(isError ? { isError: true } : {}),
            ...(errorMessage !== undefined ? { errorMessage } : {}),
        });
        // Send the result back to OpenAI so the model can continue.
        const outputPayload = isError
            ? JSON.stringify({ error: errorMessage ?? 'unknown error' })
            : JSON.stringify(result ?? null);
        this.#send({
            type: 'conversation.item.create',
            item: {
                type: 'function_call_output',
                ...(callId !== undefined ? { call_id: callId } : {}),
                output: outputPayload,
            },
        });
        this.#send({ type: 'response.create' });
        this.#setStatus('listening');
    }
    async #reportUsage(usage) {
        const inputTokens = numberOr(usage.input_tokens, 0);
        const outputTokens = numberOr(usage.output_tokens, 0);
        const inputDetailsRaw = usage.input_token_details;
        const outputDetailsRaw = usage.output_token_details;
        this.#totalInputTokens += inputTokens;
        this.#totalOutputTokens += outputTokens;
        this.#emit({
            type: 'usage',
            inputTokens,
            outputTokens,
            cumulativeUsd: this.#cumulativeUsd,
        });
        if (!this.#sessionId || !this.#model)
            return;
        try {
            await this.#opts.llm.realtime.usage.report({
                sessionId: this.#sessionId,
                model: this.#model,
                usage: {
                    inputTokens,
                    outputTokens,
                    ...(inputDetailsRaw
                        ? {
                            inputTokenDetails: {
                                ...(typeof inputDetailsRaw.text_tokens === 'number'
                                    ? { textTokens: inputDetailsRaw.text_tokens }
                                    : {}),
                                ...(typeof inputDetailsRaw.audio_tokens === 'number'
                                    ? { audioTokens: inputDetailsRaw.audio_tokens }
                                    : {}),
                                ...(typeof inputDetailsRaw.cached_tokens === 'number'
                                    ? { cachedTokens: inputDetailsRaw.cached_tokens }
                                    : {}),
                            },
                        }
                        : {}),
                    ...(outputDetailsRaw
                        ? {
                            outputTokenDetails: {
                                ...(typeof outputDetailsRaw.text_tokens === 'number'
                                    ? { textTokens: outputDetailsRaw.text_tokens }
                                    : {}),
                                ...(typeof outputDetailsRaw.audio_tokens === 'number'
                                    ? { audioTokens: outputDetailsRaw.audio_tokens }
                                    : {}),
                            },
                        }
                        : {}),
                },
            }, {
                onError: (err) => {
                    this.#emit({
                        type: 'error',
                        code: 'usage-report',
                        message: err.message ?? 'usage.report failed',
                    });
                },
            });
        }
        catch (err) {
            // .report() swallows transport errors by default; this catch is
            // a belt-and-braces guard for unexpected throws.
            this.#emit({
                type: 'error',
                code: 'usage-report',
                message: err.message,
            });
        }
    }
    // ── Helpers ─────────────────────────────────────────────────
    #send(payload) {
        const dc = this.#dc;
        if (!dc || this.#closed)
            return;
        const data = JSON.stringify(payload);
        if (dc.readyState === 'connecting') {
            this.#outbox.push(data);
            return;
        }
        if (dc.readyState !== 'open')
            return;
        this.#sendRaw(dc, data);
    }
    #flushOutbox() {
        const dc = this.#dc;
        if (!dc || this.#closed || dc.readyState !== 'open')
            return;
        for (const data of this.#outbox.splice(0))
            this.#sendRaw(dc, data);
    }
    #sendRaw(dc, data) {
        try {
            dc.send(data);
        }
        catch (err) {
            this.#emit({
                type: 'error',
                code: 'datachannel-send',
                message: err.message,
            });
        }
    }
    /** Cancel the running response and stop audio that is still playing. */
    #cutReply() {
        if (this.#responseActive)
            this.#send({ type: 'response.cancel' });
        if (this.#responseActive || this.#audioPlaying) {
            this.#send({ type: 'output_audio_buffer.clear' });
        }
    }
    #ensureAudioElement() {
        if (this.#audioElement)
            return this.#audioElement;
        if (this.#opts.audioElement) {
            this.#audioElement = this.#opts.audioElement;
            this.#ownsAudioElement = false;
            return this.#audioElement;
        }
        const audio = document.createElement('audio');
        audio.autoplay = true;
        audio.setAttribute('hidden', '');
        document.body.appendChild(audio);
        this.#audioElement = audio;
        this.#ownsAudioElement = true;
        return audio;
    }
    #setStatus(status) {
        if (this.#status === status)
            return;
        this.#status = status;
        this.#emit({ type: 'status', status });
    }
    #emit(event) {
        for (const listener of this.#listeners) {
            try {
                listener(event);
            }
            catch {
                // Listener errors must not break the dispatch loop.
            }
        }
    }
}
/** `{ responseId }` when the server event names its response. */
function responseIdOf(event) {
    return typeof event.response_id === 'string' && event.response_id
        ? { responseId: event.response_id }
        : {};
}
/** `{ itemId }` when the server event names its conversation item. */
function itemIdOf(event) {
    return typeof event.item_id === 'string' && event.item_id ? { itemId: event.item_id } : {};
}
function numberOr(value, fallback) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;
}
async function safeText(res) {
    try {
        return await res.text();
    }
    catch {
        return '<unreadable body>';
    }
}
//# sourceMappingURL=realtime-driver.js.map